import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { expirationDurations } from 'services/cookie.service'

import { useAppSelector } from 'redux/hooks'
import { useActionless } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

export type RightPanelType = 'colors' | 'probability' | 'manaCurve' | 'qunatities' | 'condensed'
export type InteractionType = 'description' | 'comments'

type RightPanelStatus = {
  open: boolean
  locked: boolean
  type: RightPanelType
}

type InteractionStatus = {
  open: boolean
  locked: boolean
  type: InteractionType
}

type StaticCardStatus = {
  open: boolean
}

export const useRightPanel = (): [RightPanelStatus, (updatedStatus: RightPanelStatus) => void] => {
  const [{ rpOpen, rpLock, rpType: type = 'condensed' }, setCookies] = useCookies(['rpOpen', 'rpLock', 'rpType'])

  const open = rpOpen === 'true'
  const locked = rpLock === 'true'

  const [rightPanelStatus, setRightPanelStatus] = useState<RightPanelStatus>({ open, locked, type })

  const handleSetNewStatus = (rightPanelStatus: RightPanelStatus) => {
    setCookies('rpOpen', `${rightPanelStatus.open}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('rpLock', `${rightPanelStatus.locked}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('rpType', rightPanelStatus.type, { path: '/', maxAge: expirationDurations.year })
  }

  useEffect(() => {
    // If they locked the panel and the screen to smaller than we'd allow the locked panel to be, unlock it
    // This is a bit un-elegant since it'll trigger a re-render immediately after the inital render, but better than having a locked panel on a phone
    if (locked && window.innerWidth < 600) handleSetNewStatus({ open: false, locked: false, type })
  }, [])

  useEffect(() => {
    setRightPanelStatus({ open, locked, type })
  }, [rpOpen, rpLock, type])

  return [rightPanelStatus, handleSetNewStatus]
}

export const usePinnedInteractions = (): [InteractionStatus, (updatedStatus: InteractionStatus) => void] => {
  const [setActiveState] = useActionless(SET_ACTIVE_STATE)
  const [{ interactionOpen, interactionLocked, interactionType: type = 'description' }, setCookies] = useCookies([
    'interactionOpen',
    'interactionLocked',
    'interactionType',
  ])

  const loadWithPrimer = useAppSelector(state => state.active.loadWithPrimer)

  const open = interactionOpen === 'open'
  const locked = interactionLocked === 'locked' || loadWithPrimer

  const [interactionPanelStatus, setInteractionPanelStatus] = useState<InteractionStatus>({ open, locked, type })

  const handleSetNewStatus = (rightPanelStatus: InteractionStatus) => {
    setCookies('interactionOpen', `${rightPanelStatus.open}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('interactionLocked', `${rightPanelStatus.locked}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('interactionType', rightPanelStatus.type, { path: '/', maxAge: expirationDurations.year })

    setActiveState({ loadWithPrimer: rightPanelStatus.locked })
  }

  useEffect(() => {
    setInteractionPanelStatus({ open, locked: interactionLocked === 'true' || loadWithPrimer, type })
  }, [interactionOpen, interactionLocked, type, loadWithPrimer])

  return [interactionPanelStatus, handleSetNewStatus]
}
